import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { OrderedSet, OrderedMap } from 'immutable';
import core, { View } from '@gin-betting/core';
import matchList from '@gin-betting/match-list';
import { getMainSpecifiersBySportId } from '@gin-betting/constants';
import {
    isMainSpecifierDifferent,
    findMainSpecifierBySportId,
} from '@gin-betting/table-view/specifiers';

import type { State } from 'types';
import { GetSpecifierHandler } from './components/SportEventRow/types';
import { ACTIVE_MARKET_STATUSES } from 'app-constants';

const {
    entities: {
        selectors: {
            selectMarkets,
            selectMatchIdFromProps,
            selectMatches,
            selectMatch,
            selectOdd,
            selectAllCompetitors,
        },
    },
} = core;

const {
    selectors: {
        selectTopMarketIdsByMatchId,
        selectTopMarketIdsMapping,
        selectMatchFetchStatuses,
        selectUserMarkets,
        selectFetchStatus: vendorSelectFetchStatus,
        selectCategoryByLocation,
    },
} = matchList;

export const selectMarketTypeFromProps = (
    _: State,
    props: { marketType: View.Market['typeId'] }
) => props.marketType;

export const selectTopMarketsFromProps = (
    _: State,
    props: { markets: View.Market[] }
) => props.markets;

export const selectSelectedMarketIdFromProps = (
    _: State,
    props: { selectedMarketId: string; matchId: string }
) => props.selectedMarketId;

export const selectTopMarketIdFromProps = (
    _: State,
    props: { topMarketId: string }
) => props.topMarketId;

export const selectGetSpecifierHandlerFromProps = (
    _: State,
    props: { getSpecifier: GetSpecifierHandler }
) => props.getSpecifier;

export const selectTopMarketsByMatchId = createCachedSelector(
    selectTopMarketIdsByMatchId,
    selectMarkets,
    (marketIds, markets): View.Market[] => {
        if (!marketIds) return [];

        return marketIds
            .toArray()
            .filter(marketId => markets.has(marketId))
            .map(marketId => markets.get(marketId)!);
    }
)(selectMatchIdFromProps);

export const selectMatchIdsWithoutTopMarketIds = createSelector(
    selectTopMarketIdsMapping,
    topMarketIdsMapping =>
        topMarketIdsMapping
            .filter(topMarketIds => topMarketIds.count() === 0)
            .keySeq()
);

export const selectMatchFetchStatus = createSelector(
    selectMatchIdFromProps,
    selectMatchFetchStatuses,
    (matchId, matchFetchStatuses) => matchFetchStatuses.get(matchId)
);

export const selectTopMarket = createSelector(
    selectMarkets,
    selectTopMarketIdFromProps,
    (markets, topMarketId) => markets.get(topMarketId)
);

export const selectMarketsByTypeId = createSelector(
    selectMarkets,
    selectMarketTypeFromProps,
    selectGetSpecifierHandlerFromProps,
    selectSelectedMarketIdFromProps,
    selectMatch,
    selectTopMarket,
    (
        markets,
        marketType,
        getSpecifierHandler,
        selectedMarketId,
        match,
        topMarket
    ) => {
        if (!match) return [];

        return match.markets
            .filter(marketId => markets.has(marketId))
            .map(marketId => markets.get(marketId)!)
            .filter(market => {
                const { typeId, status } = market;

                if (
                    topMarket &&
                    (market.id === selectedMarketId ||
                        topMarket.id === market.id)
                ) {
                    return true;
                }

                const mainSpecifierName =
                    findMainSpecifierBySportId(market.specifiers, match.sportId)
                        ?.name || getMainSpecifiersBySportId(match.sportId)[0];

                const isEqualMainSpecifiers =
                    !!mainSpecifierName &&
                    !isMainSpecifierDifferent(
                        topMarket || null,
                        market,
                        mainSpecifierName
                    );

                return (
                    typeId === marketType &&
                    ACTIVE_MARKET_STATUSES.includes(status) &&
                    topMarket &&
                    isEqualMainSpecifiers
                );
            })
            .sort((marketA, marketB) => {
                const specifierA = getSpecifierHandler(marketA.specifiers);
                const specifierB = getSpecifierHandler(marketB.specifiers);

                if (specifierA && specifierB) {
                    return (
                        Math.abs(+specifierA.value) -
                        Math.abs(+specifierB.value)
                    );
                }

                return 0;
            })
            .map(market => market.id);
    }
);

export const selectUserMarketIdByTopMarketId = createSelector(
    selectUserMarkets,
    selectTopMarketIdFromProps,
    (userMarkets, topMarketId) => userMarkets.get(topMarketId)
);

export const selectCompetitorByOddId = createSelector(
    selectOdd,
    selectAllCompetitors,
    (odd, competitors) => odd && competitors.get(odd.competitorId)
);

export const selectIsIncludeUserMarkets = createSelector(
    selectUserMarkets,
    selectTopMarketsFromProps,
    (userMarkets, topMarkets) =>
        topMarkets.some(market => userMarkets.has(market.id))
);

export const selectUserMarket = createSelector(
    selectMarkets,
    selectUserMarketIdByTopMarketId,
    (markets, userMarketId) => userMarketId && markets.get(userMarketId)
);

export const selectFetchStatus = vendorSelectFetchStatus;

export const selectGroupedCategoryMatchIdsByTournament = createSelector(
    selectCategoryByLocation,
    selectMatches,
    (outrights, allMatches) => {
        const { matchIds } = outrights;
        const outrightsByTournament = matchIds.reduce((acc, matchId) => {
            const match = allMatches.get(matchId);

            if (!match?.tournament) return acc;

            const { tournament } = match;

            const ids = acc.get(tournament) || OrderedSet<string>();

            const nextIds = ids.add(matchId);

            return acc.set(tournament, nextIds);
        }, OrderedMap<string, OrderedSet<string>>());

        return outrightsByTournament;
    }
);
